import React, { Ref } from 'react';
import classNames from 'classnames';

import focusManager from 'support/focus/FocusManager';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import { withFocusSettings } from './FocusSettingsContext';

interface FocusableProps {
  onAction?: (action: string) => boolean | string;
  onFocusSet?: (value: boolean) => void;
  className?: string | null;
  focusId?: string | number | null;
  hasInitialFocus?: boolean;
  disableFocusOnHover?: boolean;
  render: (props: { hasFocus: boolean }) => React.ReactNode;
  divRef?: Ref<HTMLDivElement> | null;
  ttsText?: string | string[] | null;
  ttsOnFocusEnabled: boolean;
  [key: string]: unknown;
}

interface FocusableState {
  hasFocus: boolean;
}

class Focusable extends React.PureComponent<FocusableProps, FocusableState> {
  static defaultProps: Partial<FocusableProps> = {
    onAction: () => false,
    onFocusSet: () => {},
    className: null,
    focusId: null,
    hasInitialFocus: false,
    disableFocusOnHover: false,
    divRef: null,
    ttsText: null,
    ttsOnFocusEnabled: true,
  };

  state: FocusableState = {
    hasFocus: false,
  };

  focusId: string | number | undefined | null = null;

  constructor(props: FocusableProps) {
    super(props);

    this.focusId = props.focusId || focusManager.getNewUniqueId();
  }

  componentDidMount() {
    const { hasInitialFocus, onFocusSet, ttsText } = this.props;

    focusManager.register(this.focusId, this.onAction, this.onFocusSet);

    if (hasInitialFocus) {
      focusManager.setCurrentFocus(this.focusId);
    }

    if (focusManager.currentFocusId === this.focusId) {
      this.setState({ hasFocus: true });
      onFocusSet?.(true);
    }

    if (ttsText && this.focusId) {
      textToSpeechManager.register(this.focusId as string, ttsText as string);
    }
  }

  componentDidUpdate() {
    const { ttsText } = this.props;
    if (ttsText && this.focusId) {
      textToSpeechManager.register(this.focusId as string, ttsText as string);
    }
  }

  componentWillUnmount() {
    focusManager.unregister(this.focusId);
    textToSpeechManager.unregister(this.focusId as string);
  }

  onAction = (action: string) => {
    const { onAction } = this.props;
    return onAction!(action);
  };

  onFocusSet = (value: boolean) => {
    const { ttsOnFocusEnabled } = this.props;
    if (value && ttsOnFocusEnabled) {
      const { ttsText } = this.props;

      if (ttsText) {
        textToSpeechManager.add(ttsText);
        textToSpeechManager.speak();
      }
    }

    const { onFocusSet } = this.props;
    this.setState({
      hasFocus: value,
    });
    onFocusSet?.(value);
  };

  onMouseEnter = () => {
    // focusManager.setCurrentFocus(this.focusId);
  };

  render() {
    const {
      onAction: _onAction,
      onFocusSet: _onFocusSet,
      className,
      divRef,
      focusId: _focusId,
      disableFocusOnHover: _disableFocusOnHover,
      render,
      hasInitialFocus: _hasInitialFocus,
      ttsText: _ttsText,
      ttsOnFocusEnabled: _ttsOnFocusEnabled,
      ...rest
    } = this.props;
    const { hasFocus } = this.state;

    return (
      <div
        ref={divRef}
        className={classNames('_fcsc', 'focusable-div', className)}
        data-fcsid={this.focusId}
        // onMouseEnter={() => {
        //   if (!disableFocusOnHover) {
        //     this.onMouseEnter();
        //   }
        // }}
        {...rest}
      >
        {render({ hasFocus })}
      </div>
    );
  }
}

const EnhancedFocusable = withFocusSettings<FocusableProps>(Focusable);

export default React.forwardRef<HTMLDivElement, Omit<FocusableProps, 'divRef' | 'ttsOnFocusEnabled'>>((props, ref) => (
  <EnhancedFocusable divRef={ref} {...props} />
));
